import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatBRL, STATUS_STYLES, DOC_TYPES } from "@/lib/constants";
import { ChevronRight, ChevronDown, PenLine, Send } from "lucide-react";
import { toast } from "sonner";
import { DashboardCharts } from "@/components/DashboardCharts";
import { useAuth } from "@/context/AuthContext";
import { DOC_TYPE_OPTIONS } from "@/lib/docTypeOptions";

const MAIN = [
  ["budget", "Orçamento", "Crie um orçamento profissional."],
  ["nota_servico", "Nota de Serviço", "Registre o serviço realizado."],
  ["report", "Laudo Técnico", "Descreva diagnóstico e solução."],
  ["contrato", "Contrato", "Crie um contrato profissional."],
  ["proposta_comercial", "Proposta", "Crie uma proposta comercial."],
];

const Stat = ({ label, value, testid }) => (
  <div className="py-3" data-testid={testid}><p className="font-display font-bold text-lg sm:text-xl leading-none">{value}</p><p className="text-xs text-muted-foreground mt-1">{label}</p></div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [sub, setSub] = useState(null);
  const [charts, setCharts] = useState(false);
  const { user } = useAuth();

  useEffect(() => {
    api.get("/dashboard").then(({ data }) => setData(data));
    api.get("/subscription").then(({ data }) => setSub(data)).catch(() => {});
  }, []);

  if (!data) return <div className="h-64 flex items-center justify-center"><div className="h-8 w-8 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>;

  return (
    <div className="max-w-3xl mx-auto space-y-10 sm:space-y-12" data-testid="home">
      {sub?.is_trial && sub?.trial_active && (
        <Link to="/plans" data-testid="trial-banner" className="flex items-center justify-between gap-3 rounded-xl border border-border px-4 py-2.5 text-sm hover:bg-accent transition-colors">
          <span>Teste termina em <b>{sub.trial_days_left} dia(s)</b></span><span className="text-primary font-semibold">Assinar</span>
        </Link>
      )}
      {sub && !sub.is_trial && sub.effective_plan === "free" && (
        <Link to="/plans" data-testid="trial-expired-banner" className="flex items-center justify-between gap-3 rounded-xl border border-amber-500/40 bg-amber-500/5 px-4 py-2.5 text-sm">
          <span>Seu período de teste terminou</span><span className="text-primary font-semibold">Ver planos</span>
        </Link>
      )}

      <header>
        <h1 className="font-display font-bold text-2xl sm:text-3xl tracking-tight" data-testid="home-greeting">Olá, {(user?.name || "").split(" ")[0]}</h1>
        <p className="text-muted-foreground mt-1">Crie seus documentos profissionais em poucos passos.</p>
      </header>

      <section data-testid="home-create">
        <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Criar documento</p>
        <div className="grid sm:grid-cols-2 gap-2.5">
          {MAIN.map(([type, label, desc]) => {
            const o = DOC_TYPE_OPTIONS.find((x) => x.type === type);
            return (
              <Link key={type} to={`/documents/new/${type}`} data-testid={`quick-${type}`}
                className="group flex items-center gap-4 rounded-2xl border border-border bg-card p-4 sm:p-5 hover:border-foreground/30 transition-colors active:scale-[0.99]">
                <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${o?.color}`}><o.Icon className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1"><p className="font-semibold">{label}</p><p className="text-sm text-muted-foreground truncate">{desc}</p></div>
                <ChevronRight className="h-5 w-5 text-muted-foreground/60 group-hover:text-foreground transition-colors" />
              </Link>
            );
          })}
        </div>
      </section>

      {data.awaiting_signature?.length > 0 && (
        <section data-testid="awaiting-signature">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3 flex items-center gap-1.5"><PenLine className="h-3.5 w-3.5" /> Aguardando assinatura ({data.awaiting_signature.length})</p>
          <div className="divide-y divide-border rounded-2xl border border-border overflow-hidden">
            {data.awaiting_signature.slice(0, 5).map((d) => {
              const wa = (d.client_whatsapp || "").replace(/\D/g, "");
              const waNum = wa && wa.length <= 11 ? `55${wa}` : wa;
              const link = `${window.location.origin}/doc/${d.share_token}`;
              const msg = encodeURIComponent(`Olá ${d.client_name}! Seu ${DOC_TYPES[d.type]?.label} ${d.number} ainda está aguardando sua assinatura.\nVisualize e assine aqui: ${link}`);
              const resend = (e) => { e.preventDefault(); if (waNum) window.open(`https://wa.me/${waNum}?text=${msg}`, "_blank"); else { navigator.clipboard.writeText(link); toast.success("Link copiado! Envie ao cliente."); } };
              return (
                <div key={d.id} className="flex items-center gap-3 p-3 bg-card" data-testid={`awaiting-${d.id}`}>
                  <Link to={`/documents/${d.id}`} className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{DOC_TYPES[d.type]?.label} {d.number} · {d.client_name}</p></Link>
                  <Button size="sm" variant="outline" className="h-9 shrink-0" onClick={resend} data-testid={`resend-${d.id}`}><Send className="h-3.5 w-3.5 mr-1" /> Reenviar</Button>
                </div>
              );
            })}
          </div>
        </section>
      )}

      <section data-testid="home-stats">
        <div className="grid grid-cols-2 sm:grid-cols-4 divide-x divide-border rounded-2xl border border-border bg-card px-4 [&>div]:px-3 [&>div:first-child]:pl-0">
          <Stat label="Documentos este mês" value={data.docs_month ?? 0} testid="metric-docs-month" />
          <Stat label="Orçamentos" value={data.budgets ?? 0} testid="metric-budgets" />
          <Stat label="Serviços realizados" value={data.completed ?? 0} testid="metric-completed" />
          <Stat label="Valor recebido" value={formatBRL(data.received)} testid="metric-received" />
        </div>
        <button type="button" onClick={() => setCharts((v) => !v)} className="mt-2 text-xs text-muted-foreground hover:text-foreground flex items-center gap-1 h-8" data-testid="toggle-charts">
          <ChevronDown className={`h-3.5 w-3.5 transition-transform ${charts ? "rotate-180" : ""}`} /> {charts ? "Ocultar gráficos" : "Ver gráficos"}
        </button>
        {charts && <div className="mt-2"><DashboardCharts /></div>}
      </section>

      <section data-testid="home-recent">
        <div className="flex items-center justify-between mb-3">
          <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Recentes</p>
          <Link to="/documents" className="text-sm text-primary font-semibold" data-testid="view-all-docs">Ver todos</Link>
        </div>
        {data.recent?.length ? (
          <div className="divide-y divide-border rounded-2xl border border-border bg-card overflow-hidden">
            {data.recent.slice(0, 5).map((d) => (
              <Link to={`/documents/${d.id}`} key={d.id} data-testid={`recent-doc-${d.id}`} className="flex items-center gap-3 p-3 hover:bg-accent/50 transition-colors">
                <div className="min-w-0 flex-1"><p className="font-medium text-sm truncate">{DOC_TYPES[d.type]?.label} {d.number}</p><p className="text-xs text-muted-foreground truncate">{d.client_name}</p></div>
                <div className="text-right shrink-0">{Number(d.total) > 0 && <p className="text-sm font-semibold">{formatBRL(d.total)}</p>}<p className={`text-[11px] ${STATUS_STYLES[d.status] ? "" : "text-muted-foreground"}`}>{d.status}</p></div>
              </Link>
            ))}
          </div>
        ) : (
          <p className="text-sm text-muted-foreground" data-testid="home-empty">Nenhum documento ainda. Escolha um tipo acima para começar.</p>
        )}
      </section>
    </div>
  );
}
